import FullCalendar from '@fullcalendar/react'
import { dateFormat } from 'components/calendar/CalendarHeader'
import dayjs, { Dayjs } from 'dayjs'
import usePersistState from 'hooks/usePersistState'
import {
  ComponentRef,
  createContext,
  ReactNode,
  Ref,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

type FullCalendarRef = ComponentRef<typeof FullCalendar>
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import i18n from 'i18n'
import { t } from 'i18next'
import Database from 'services/db/Database'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { GroupDoc } from 'types/api/changes'
import {
  CalendarRange,
  getCalendarRangeByString
} from 'types/components/calendar'
import { Option } from 'types/components/form'
import { GoogleCalendar, ICalendar } from 'types/google_events'
import { CollectionType } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { prefixGoogleEventsGroup } from 'utils/constants'
import { defaultGroupId, ProviderType } from 'utils/groups'
import { isTrulyEmpty } from 'utils/object'
import { isGuest } from 'utils/user'

import { useCalendarSync } from './CalendarSyncProdivder'
import { setDefaultCalendarIfNeeded } from './GetChangesProvider'

interface CalendarContextProps {
  ref: Ref<FullCalendarRef>
  view: CalendarRange
  groupServerID: string
  onGoToToday: () => void
  onView: (view: CalendarRange) => void
  next: () => void
  prev: () => void
  onGoToDate: (date: Date | string) => void
  onGroupServer: (id: string) => void
  currentMonth: string
  onCurrentMonth: (month: string) => void
  monthArray: Option[]
  onSetMonthArray: (array: Option[]) => void
  groupInfo: string | undefined
  onGroupInfo: (id: string | undefined) => void
  initiateGoogleSync: (
    onComplete?: (calendar: ICalendar) => void,
    onCancel?: () => void,
    startedProcess?: () => void
  ) => void
  weekArray: Option[]
  scrollToTime: Dayjs
  onScrollToTime: (d: Dayjs) => void
  fromISO: string | undefined
  toISO: string | undefined
  onFromISO: (date: string | undefined) => void
  onToISO: (date: string | undefined) => void
  onSetFromTo: (from: string | undefined, to: string | undefined) => void
  scrollToDate: Dayjs
  selectedGroup: GroupDoc | null
}
interface CalendarProviderProps {
  children: ReactNode
}

const CalendarContext = createContext<CalendarContextProps>({
  ref: { current: {} as FullCalendarRef },
  view: CalendarRange.MONTH,
  groupServerID: '',
  onGoToToday: () => {},
  onView: () => {},
  next: () => {},
  prev: () => {},
  onGoToDate: () => {},
  onGroupServer: () => {},
  currentMonth: '',
  onCurrentMonth: () => {},
  onSetMonthArray: () => {},
  monthArray: [],
  groupInfo: undefined,
  onGroupInfo: () => {},
  initiateGoogleSync: () => {},
  weekArray: [],
  scrollToTime: dayjs(),
  onScrollToTime: (d: Dayjs) => {},
  fromISO: undefined,
  toISO: undefined,
  onFromISO: () => {},
  onToISO: () => {},
  onSetFromTo: () => {},
  scrollToDate: dayjs(),
  selectedGroup: null
})

export const CALENDAR_VIEW = {
  [CalendarRange.DAY]: 'timeGridDay',
  [CalendarRange.WEEK]: 'timeGridWeek',
  [CalendarRange.MONTH]: 'dayGridMonth',
  [CalendarRange.LISTWEEK]: 'listMonth',
  [CalendarRange.LISTDAY]: 'listDay',
  [CalendarRange.CARDS]: 'galleryMonth'
}

export default function CalendarProvider(props: CalendarProviderProps) {
  const ref = useRef<FullCalendarRef>(null)

  const [visibleRange, setVisibleRange] = useState<{
    from: string | undefined
    to: string | undefined
  }>({ from: undefined, to: undefined })

  const onSetFromTo = useCallback(
    (from: string | undefined, to: string | undefined) => {
      setVisibleRange((prev) =>
        prev.from === from && prev.to === to ? prev : { from, to }
      )
    },
    []
  )

  const onFromISO = useCallback((date: string | undefined) => {
    setVisibleRange((prev) => (prev.from === date ? prev : { ...prev, from: date }))
  }, [])

  const onToISO = useCallback((date: string | undefined) => {
    setVisibleRange((prev) => (prev.to === date ? prev : { ...prev, to: date }))
  }, [])
  const [scrollToDate, onScrollToDate] = useState<Dayjs>(dayjs())
  const [scrollToTime, onScrollToTime] = useState<Dayjs>(dayjs())
  const [monthArray, onSetMonthArray] = useState<Option[]>(buildMonthsArray())
  const [weekArray, onSetWeekArray] = useState<Option[]>(buildWeekArray())
  const [groupServerID, onGroupServerID] = usePersistState(
    '',
    LocalStorageKey.SELECTED_GROUP
  )
  window.onGroupServerID = onGroupServerID
  const [groupInfo, onGroupInfoChange] = useState<string | undefined>(undefined)

  const onGroupInfo = useCallback((id: string | undefined) => {
    Logger.blue('CalendarProvider:onGroupInfo', id)
    onGroupInfoChange(id)
  }, [])

  const [currentView, onCurrentView] = useState(
    getCalendarRangeByString(
      LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ?? ''
    ) ??
      (LocalStorage.get(LocalStorageKey.PHONE_NUMBER)
        ? CalendarRange.WEEK
        : CalendarRange.MONTH)
  )

  useEffect(() => {
    LocalStorage.set(LocalStorageKey.CALENDAR_VIEW, currentView)
  }, [currentView])

  const [currentMonth, onCurrentMonth] = useState<string>(
    dayjs(Date.now()).set('date', 1).format(dateFormat)
  )

  const switchMonthIfNeeded = useCallback((isNext: boolean) => {
    const currentMonthDayJS = dayjs(ref.current?.getApi().getDate()).set(
      'date',
      1
    )
    const nextMonth = currentMonthDayJS.format(dateFormat)

    onSetMonthArray((prev) => {
      if (prev.find((option) => option.value === nextMonth)) return prev
      const newMonth = {
        label: currentMonthDayJS.format('MMM YYYY'),
        value: nextMonth
      }
      return isNext ? [...prev, newMonth] : [newMonth, ...prev]
    })

    onCurrentMonth((prev) => (prev === nextMonth ? prev : nextMonth))
  }, [])

  useEffect(() => {
    Logger.blue(
      'CalendarProvider:ScrollToTime',
      scrollToTime
        .subtract(scrollToTime.get('hour') > 0 ? 1 : 0, 'hour')
        .format('HH:mm')
    )
    Logger.blue('CalendarProvider:ScrollToTime', scrollToTime.format('HH:mm'))
    ref.current
      ?.getApi()
      ?.scrollToTime(scrollToTime.subtract(1, 'hour').format('HH:mm'))
  }, [scrollToTime])

  const onView = useCallback((view: CalendarRange) => {
    onCurrentView(view)
    if (CALENDAR_VIEW[view])
      ref.current?.getApi()?.changeView(CALENDAR_VIEW[view])
  }, [])

  const next = useCallback(() => {
    if (isGuest()) {
      // for guests, we clear the database inbetween months
      return Database.clearCollection(CollectionType.EVENT).then(() => {
        ref.current?.getApi()?.next()
        switchMonthIfNeeded(true)
      })
    } else {
      ref.current?.getApi()?.next()
      switchMonthIfNeeded(true)
    }
  }, [switchMonthIfNeeded])

  const prev = useCallback(() => {
    if (isGuest()) {
      // for guests, we clear the database inbetween months
      return Database.clearCollection(CollectionType.EVENT).then(() => {
        ref.current?.getApi()?.prev()
        switchMonthIfNeeded(false)
      })
    } else {
      ref.current?.getApi()?.prev()
      switchMonthIfNeeded(false)
    }
  }, [switchMonthIfNeeded])

  const onGroupServer = useCallback(
    (id: string) => {
      LocalStorage.set(LocalStorageKey.SELECTED_GROUP, id)
      onGroupServerID(id)

      if (isTrulyEmpty(id)) {
        onGroupInfoChange(undefined)
      }
    },
    [onGroupServerID]
  )

  const [defaultGroup, setDefaultGroup] = useState<GroupDoc | null>(null)

  // effect for selected group
  useEffect(() => {
    if (isTrulyEmpty(LocalStorage.get(LocalStorageKey.USER_ID))) return
    if (groupServerID) {
      localGroupById(groupServerID).then((group) => {
        Logger.orange('CalendarProvider:DefaultGroup', group)
        if (group) {
          setDefaultGroup(group)
        }
      })
    } else {
      UserSettings()
        .then((userSettings) => {
          return defaultGroupId(userSettings)
        })
        .then((defaultGroupId) => {
          return localGroupById(defaultGroupId ?? '')
        })
        .then((group) => {
          Logger.orange('CalendarProvider:DefaultGroup', group)
          if (group) {
            setDefaultGroup(group)
          } else {
            setDefaultGroup(null)
          }
        })
    }
  }, [groupServerID])

  const onGoToToday = useCallback(() => {
    ref.current?.getApi()?.today()

    const todayMonth = dayjs().startOf('month').format(dateFormat)
    onCurrentMonth((prev) => (prev === todayMonth ? prev : todayMonth))
    onScrollToTime(dayjs())
  }, [])

  const onGoToDate = useCallback((date: Date | string) => {
    Logger.blue('CalendarProvider:GoToDate', date)

    try {
      ref.current?.getApi().gotoDate(date)
    } catch (e) {
      Logger.red(`${e}`)
    }
    onScrollToDate(dayjs(date))
    const targetMonth = dayjs(date).set('date', 1).format(dateFormat)
    onCurrentMonth((prev) => (prev === targetMonth ? prev : targetMonth))
    onScrollToTime(dayjs(date))
  }, [])

  window.goToDate = onGoToDate

  const calendarSync = useCalendarSync()

  const [showNonGrantedGoogleCalendar, onShowNonGrantedGoogleCalendar] =
    useState(false)

  const onGrantedCalendarAccess = useCallback(
    (calendar: ICalendar) => {
      onGroupServer(`${prefixGoogleEventsGroup}${calendar.id}`)
    },
    [onGroupServer]
  )

  const initiateGoogleSync = useCallback(
    (
      onComplete?: (calendar: ICalendar) => void,
      onCancel?: () => void,
      startedProcess?: () => void
    ) => {
      const success = (googleCalendar: ICalendar) => {
        onGrantedCalendarAccess(googleCalendar)
        if (onComplete) onComplete(googleCalendar)

        if (is24meApp()) setDefaultCalendarIfNeeded()
      }

      const cancel = () => {
        onShowNonGrantedGoogleCalendar(true)
        if (onCancel) onCancel()
      }

      const process = () => {
        if (startedProcess) startedProcess()
      }

      calendarSync.connect(ProviderType.GOOGLE, success, cancel, process)

      onShowNonGrantedGoogleCalendar(false)
    },
    [calendarSync, onGrantedCalendarAccess]
  )

  const hideNonGrantedGoogleCal = useCallback(() => {
    onShowNonGrantedGoogleCalendar(false)
  }, [])

  const context = useMemo<CalendarContextProps>(
    () => ({
      ref: ref as Ref<FullCalendarRef>,
      view: currentView,
      groupServerID,
      onGoToToday,
      onView,
      next,
      prev,
      onGroupServer,
      onGoToDate,
      currentMonth,
      onCurrentMonth,
      monthArray,
      onSetMonthArray,
      groupInfo,
      onGroupInfo,
      initiateGoogleSync,
      weekArray: [],
      scrollToTime,
      onScrollToTime,
      fromISO: visibleRange.from,
      toISO: visibleRange.to,
      onFromISO,
      onToISO,
      onSetFromTo,
      scrollToDate,
      selectedGroup: defaultGroup
    }),
    [
      currentView,
      groupServerID,
      onGoToToday,
      onView,
      next,
      prev,
      onGroupServer,
      onGoToDate,
      currentMonth,
      monthArray,
      groupInfo,
      onGroupInfo,
      initiateGoogleSync,
      scrollToTime,
      visibleRange.from,
      visibleRange.to,
      onFromISO,
      onToISO,
      onSetFromTo,
      scrollToDate,
      defaultGroup
    ]
  )

  return (
    <CalendarContext.Provider value={context}>
      {props.children}
      {showNonGrantedGoogleCalendar && (
        <ConfirmationDialog
          title={`${t('noGooglePermissionsTitle')}`}
          content={`${t('noGooglePermissionsMessage', {
            app: is24meApp() ? t('appName24me') : t('appName')
          })}`}
          rightButton={`${t('retry')}`}
          rightButtonColor={APP_COLOR}
          leftButton={`${t('cancel')}`}
          handleRightButton={() => {
            calendarSync.connect(ProviderType.GOOGLE, onGrantedCalendarAccess)
          }}
          handleLeftButton={hideNonGrantedGoogleCal}
          handleClose={hideNonGrantedGoogleCal}
          leftButtonColor="black"
          open={showNonGrantedGoogleCalendar}
        />
      )}
    </CalendarContext.Provider>
  )
}

export function useCalendar(): CalendarContextProps {
  return useContext(CalendarContext)
}

function buildMonthsArray(): Option[] {
  const months: Option[] = []
  const now = dayjs(Date.now()).set('date', 1).locale(i18n.language)

  for (let index = 2; index >= 0; index--) {
    const prevMonth = now.add(index * -1, 'month')
    months.push({
      label: prevMonth.format('MMM YYYY'),
      value: prevMonth.format(dateFormat)
    })
  }

  for (let index = 1; index < 12; index++) {
    const nextMonth = now.add(index, 'month')
    months.push({
      label: nextMonth.format('MMM YYYY'),
      value: nextMonth.format(dateFormat)
    })
  }

  return months
}

function buildWeekArray(): Option[] {
  const weeks: Option[] = []
  const now = dayjs(Date.now()).set('date', 1).locale(i18n.language)

  for (let index = 2; index >= 0; index--) {
    const prevWeek = now.add(index * -1, 'month')
    weeks.push({
      label: prevWeek.format('MMM YYYY'),
      value: prevWeek.format(dateFormat)
    })
  }

  for (let index = 1; index < 12; index++) {
    const nextWeek = now.add(index, 'month')
    weeks.push({
      label: nextWeek.format('MMM YYYY'),
      value: nextWeek.format(dateFormat)
    })
  }

  return weeks
}
